import React from 'react';
import Icon from '../../../components/AppIcon';

const RelationshipAnalytics = ({ analytics, selectedNode, links }) => {
  const getNodeConnections = (node) => {
    if (!node || !links) return [];
    
    return links?.filter(
      l => l?.source === node?.id || l?.target === node?.id ||
           l?.source?.id === node?.id || l?.target?.id === node?.id
    )?.map(link => {
      const isSource = link?.source === node?.id || link?.source?.id === node?.id;
      const connectedId = isSource 
        ? (typeof link?.target === 'object' ? link?.target?.id : link?.target)
        : (typeof link?.source === 'object' ? link?.source?.id : link?.source);
      
      return {
        character: connectedId,
        strength: link?.value
      };
    })?.sort((a, b) => b?.strength - a?.strength);
  };

  const nodeConnections = selectedNode ? getNodeConnections(selectedNode) : [];

  return (
    <div className="space-y-4">
      {/* Network Statistics */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Network Statistics</h3>
        </div>

        {analytics ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Total Characters</span>
              <span className="text-lg font-bold text-foreground">{analytics?.totalCharacters}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Total Connections</span>
              <span className="text-lg font-bold text-foreground">{analytics?.totalConnections}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Network Density</span>
              <span className="text-lg font-bold text-primary">{analytics?.networkDensity}%</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Avg. Connections</span>
              <span className="text-lg font-bold text-foreground">{analytics?.averageConnections}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No analytics available</p>
        )}
      </div>

      {/* Character Centrality */}
      {analytics?.topCentralCharacters && analytics?.topCentralCharacters?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Crown" size={20} className="text-yellow-500" />
            <h3 className="font-semibold text-foreground">Most Central Characters</h3>
          </div>

          <div className="space-y-2">
            {analytics?.topCentralCharacters?.map((char, index) => (
              <div key={index} className="flex items-center gap-3 p-2 bg-muted rounded-lg">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{char?.name}</p>
                  <p className="text-xs text-muted-foreground">{char?.connections} connections</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Node Details */}
      {selectedNode && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="User" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Selected Character</h3>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="font-bold text-lg text-foreground mb-1">{selectedNode?.name}</h4>
              <p className="text-sm text-muted-foreground">Lines: {selectedNode?.lineCount}</p>
            </div>

            {nodeConnections?.length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-foreground mb-2">Connections ({nodeConnections?.length})</h5>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {nodeConnections?.map((conn, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-xs">
                      <span className="text-foreground font-medium">{conn?.character}</span>
                      <span className="text-muted-foreground">{conn?.strength} scenes</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Info" size={18} className="text-muted-foreground" />
          <h3 className="font-semibold text-foreground text-sm">How to Use</h3>
        </div>
        <ul className="space-y-2 text-xs text-muted-foreground">
          <li className="flex items-start gap-2">
            <Icon name="MousePointer" size={14} className="mt-0.5 flex-shrink-0" />
            <span>Click nodes to view character details</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Move" size={14} className="mt-0.5 flex-shrink-0" />
            <span>Drag nodes to rearrange the network</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="ZoomIn" size={14} className="mt-0.5 flex-shrink-0" />
            <span>Scroll to zoom in/out</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Link" size={14} className="mt-0.5 flex-shrink-0" />
            <span>Thicker lines = more shared scenes</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RelationshipAnalytics;